import type { ChangeEvent } from "react"
import { UnlinkIcon } from "components/ui/icons/unlink/UnlinkIcon"
import { ArrowUpRightIcon } from "components/ui/icons/arrowUpRight/ArrowUpRightIcon"
import { TriangleAlertIcon } from "components/ui/icons/Warning/TriangleAlertIcon"
import style from "./ElevationReferenceRow.module.scss"

export type TElevationRowState = "default" | "filled" | "connected" | "error"

type Props = {
	elevationNumber: string
	connectedPageName?: string
	referenceLabel: string
	state: TElevationRowState
	connectPending?: boolean
	onElevationNumberChange: (next: string) => void
	onReferenceLabelChange: (next: string) => void
	onElevationNumberBlur?: () => void
	onReferenceLabelBlur?: () => void
	onRemove: () => void
	onConnect: () => void
	onUnlink: () => void
	onNavigateToEref?: () => void
}

const RemoveIcon = () => (
	<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M2 2L10 10M2 10L10 2" stroke="#000" strokeWidth="1.5" strokeLinecap="round" />
	</svg>
)

export const ElevationReferenceRow = ({
	elevationNumber,
	connectedPageName,
	referenceLabel,
	state,
	connectPending = false,
	onElevationNumberChange,
	onReferenceLabelChange,
	onElevationNumberBlur,
	onReferenceLabelBlur,
	onRemove,
	onConnect,
	onUnlink,
	onNavigateToEref,
}: Props) => {
	const handleElevationChange = (e: ChangeEvent<HTMLInputElement>) => onElevationNumberChange(e.target.value)
	const handleLabelChange = (e: ChangeEvent<HTMLInputElement>) => onReferenceLabelChange(e.target.value)

	const renderConnectionSlot = () => {
		if (state === "connected") {
			return (
				<>
					<button
						type="button"
						className={`${style.connectedLabel} ${onNavigateToEref ? style.connectedLabelInteractive : ""}`}
						onClick={onNavigateToEref}
						disabled={!onNavigateToEref}
						aria-label="Navigate to elevation"
					>
						<span className={style.dot} />
						<span className={style.connectedLabelText}>
							{connectedPageName ? `${elevationNumber} – ${connectedPageName}` : elevationNumber}
						</span>
						<span className={style.connectedLabelArrow} aria-hidden="true">
							<ArrowUpRightIcon />
						</span>
					</button>
					<button type="button" className={style.iconButton} onClick={onUnlink} aria-label="Unlink elevation">
						<UnlinkIcon stroke="#000" />
					</button>
				</>
			)
		}

		if (state === "error") {
			return (
				<>
					<button
						type="button"
						className={`${style.connectButton} ${style.connectButtonActive}`}
						onClick={onConnect}
						disabled={connectPending}
					>
						{connectPending ? "Connecting…" : "Retry"}
					</button>
					<span className={style.slot}>
						<TriangleAlertIcon />
					</span>
				</>
			)
		}

		const disabled = state === "default" || connectPending
		return (
			<>
				<button
					type="button"
					className={`${style.connectButton} ${disabled ? style.connectButtonDisabled : style.connectButtonActive}`}
					onClick={onConnect}
					disabled={disabled}
				>
					{connectPending ? "Connecting…" : "Search and connect"}
				</button>
				<span className={style.slot} aria-hidden="true" />
			</>
		)
	}

	return (
		<div className={style.row}>
			<div className={style.field}>
				<span className={style.label}>Elevation number</span>
				<div className={style.control}>
					<input
						className={style.input}
						value={elevationNumber}
						onChange={handleElevationChange}
						onBlur={onElevationNumberBlur}
						placeholder=""
					/>
					<button type="button" className={style.iconButton} onClick={onRemove} aria-label="Remove elevation">
						<RemoveIcon />
					</button>
				</div>
			</div>

			<div className={style.field}>
				<span className={style.label}>Reference label</span>
				<div className={style.control}>
					<input
						className={style.input}
						value={referenceLabel}
						onChange={handleLabelChange}
						onBlur={onReferenceLabelBlur}
						placeholder=""
					/>
					<span className={style.slot} aria-hidden="true" />
				</div>
			</div>

			<div className={style.field}>
				<span className={style.label}>Elevation</span>
				<div className={style.control}>{renderConnectionSlot()}</div>
			</div>
		</div>
	)
}
